import lzma from "lzma/src/lzma_worker.js";
import { inflateSync } from "fflate";
import { Binary, canonical } from "./binary.js";
import { logger } from "./log.js";
const log = logger("Compression");
const MAX_OUTPUT = 256 * 1024 * 1024;
export function lzmaDecode(properties, payload, length) {
    log.debug("LZMA decode", { compressedBytes: payload.length, outputBytes: length });
    if (!Number.isSafeInteger(length) || length < 0 || length > MAX_OUTPUT)
        throw new Error("LZMA output exceeds budget");
    const dictionary = new DataView(
        properties.buffer,
        properties.byteOffset,
        properties.byteLength,
    ).getUint32(1, true);
    if (dictionary > MAX_OUTPUT)
        throw new Error("LZMA dictionary exceeds budget");
    const data = new Uint8Array(13 + payload.length);
    data.set(properties);
    // A complete lump cannot reference history older than its output. Source
    // commonly declares a 16 MiB dictionary even for tiny lumps; the decoder's
    // sparse JS arrays become prohibitively slow at that size.
    new DataView(data.buffer).setUint32(
        1,
        Math.min(dictionary, Math.max(4096, length)),
        true,
    );
    new DataView(data.buffer).setUint32(5, length, true);
    data.set(payload, 13);
    const result = lzma.LZMA.decompress(data);
    const bytes =
        typeof result === "string"
            ? new TextEncoder().encode(result)
            : Uint8Array.from(result);
    if (bytes.length !== length) throw new Error("LZMA length mismatch");
    log.debug("LZMA complete", { bytes: bytes.length });
    return bytes;
}
export function valveLump(bytes) {
    const r = new Binary(bytes);
    if (bytes.length < 4 || r.text(0, 4) !== "LZMA") return bytes;
    const length = r.u32(4),
        compressed = r.u32(8);
    return lzmaDecode(r.slice(12, 5), r.slice(17, compressed), length);
}
export class PakFile {
    constructor(bytes, diagnostic = () => {}) {
        this.r = new Binary(bytes);
        this.entries = new Map();
        if (!bytes.length) return;
        let end = bytes.length - 22;
        while (
            end >= Math.max(0, bytes.length - 65557) &&
            this.r.u32(end) !== 0x06054b50
        )
            end--;
        if (end < Math.max(0, bytes.length - 65557))
            throw new Error("ZIP directory not found");
        let offset = this.r.u32(end + 16);
        const count = this.r.u16(end + 10);
        let ignored = 0;
        if (this.r.u16(end + 4) || this.r.u16(end + 6) || count === 65535)
            throw new Error("Multipart/ZIP64 pakfiles are unsupported");
        for (let i = 0; i < count; i++) {
            if (this.r.u32(offset) !== 0x02014b50)
                throw new Error("Invalid ZIP directory");
            const flags = this.r.u16(offset + 8),
                method = this.r.u16(offset + 10),
                compressed = this.r.u32(offset + 20),
                size = this.r.u32(offset + 24);
            const nameLength = this.r.u16(offset + 28),
                extra = this.r.u16(offset + 30),
                comment = this.r.u16(offset + 32),
                local = this.r.u32(offset + 42);
            const name = this.r.text(offset + 46, nameLength);
            if (!name.endsWith("/")) {
                // Old map packers can include the author's absolute build paths.
                // Keep those entries inaccessible without losing the valid map assets.
                let path;
                try { path = canonical(name); }
                catch { ignored++; }
                if (flags & 1 || size > MAX_OUTPUT || (path && this.entries.has(path)))
                    throw new Error("Unsafe ZIP entry");
                if (path) this.entries.set(path, { method, compressed, size, local });
            }
            offset += 46 + nameLength + extra + comment;
        }
        if (ignored) {
            log.warn("invalid embedded paths ignored", { entries: ignored });
            diagnostic(`Ignored ${ignored} embedded ${ignored === 1 ? "file" : "files"} with invalid Source asset paths.`);
        }
        log.debug("pak indexed", { bytes: bytes.length, entries: this.entries.size });
    }
    has(path) {
        return this.entries.has(canonical(path));
    }
    get(path) {
        const entry = this.entries.get(canonical(path));
        if (!entry) return null;
        const { method, compressed, size, local } = entry,
            r = this.r;
        log.debug("pak extract", { path, method, compressedBytes: compressed, outputBytes: size });
        if (r.u32(local) !== 0x04034b50)
            throw new Error("Invalid ZIP local entry");
        const data = r.slice(
            local + 30 + r.u16(local + 26) + r.u16(local + 28),
            compressed,
        );
        let result;
        if (method === 0) result = data;
        else if (method === 8)
            result = inflateSync(data, { out: new Uint8Array(size) });
        else if (method === 14) {
            const p = new Binary(data);
            if (p.u16(2) !== 5) throw new Error("Invalid ZIP LZMA properties");
            result = lzmaDecode(
                p.slice(4, 5),
                p.slice(9, data.length - 9),
                size,
            );
        } else throw new Error(`Unsupported ZIP method ${method}`);
        if (result.length !== size) throw new Error("ZIP length mismatch");
        return result;
    }
}
